  import React, { useEffect, useState } from 'react';
  import { usePathname, useRouter } from 'next/navigation';
  import { formFields, reviewFormFields } from '@/constants/constants';
  import { useGlobalContext } from '@/context/GlobalContext';

  import apiService from '@/lib/api/apiServices';

  const useLeadFormModalHook = ({
    setIsOpen,
    isStudyAbroad,
  }: { setIsOpen?: (value: boolean) => void; isStudyAbroad?: boolean } = {}) => {
    const initialFormData = {
      name: '',
      email: '',
      phone_number: '',
      city: '',
      state: '',
      course: '',
      country: null,
      year: '',
      is_online_intrested: false,
    };

    const { leadFormData } = useGlobalContext();

    const { source, country, college, exam, course, title, isAbroadCountry } = leadFormData;

    const router = useRouter();
    const pathname = usePathname();
    const sources = pathname === '/' ? 'homepage' : pathname;
    // const sources =  router?.asPath === "/" ? "homepage" : router?.asPath;

    const currentYear = new Date().getFullYear();
    const intakeYears = [currentYear, currentYear + 1, currentYear + 2];
    const modalFields = source === 'college-review' ? reviewFormFields : formFields;

    const [submitted, setSubmitted] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otp, setOtp] = useState('');
    const [formData, setFormData] = useState<any>(initialFormData);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [timer, setTimer] = useState(30);
    const [showResend, setShowResend] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      setFormData((prevData: any) => ({
        ...prevData,
        [name]: value,
      }));
    };

    const handleToggleCourse = () => {
      setFormData((prevData: any) => ({
        ...prevData,
        is_online_intrested: !prevData.is_online_intrested,
      }));
    };
    const handleClose = () => {
      setIsOpen?.(false);
      setFormData(initialFormData);
      setOtpSent(false);
      setOtp('');
      setTimer(30);
      setShowResend(false);
    };

    const verifyOtp = async () => {
      try {
        const response = await apiService.post('/lead-form/verify-otp', {
          data: {
            name: formData.name,
            phone_number: formData.phone_number,
            email: formData.email,
            city: formData.city ?? null,
            course: course ?? formData.course ?? null,
            otp: otp,
          },
        });
        if (response?.data?.message === 'OTP Verified Successfully') {
          setSubmitted(true);
          sessionStorage.setItem('leadFormSubmission', JSON.stringify({ submitted: true, returnUrl: pathname }));
          handleClose();
          router.push('/thank-you');
        }
      } catch (error: any) {
        console.error('OTP verification error:', error);
        setErrorMessage(error?.response?.data?.error?.message);
      }
    };

    const submitLeadForm = async () => {
      try {
        const response = await apiService.post('/lead-forms', {
          data: {
            name: formData.name,
            email: formData.email,
            phone_number: formData.phone_number,
            source: sources ?? null,
            is_online_intrested: formData.is_online_intrested ?? false,
            is_internal_lead: true,
            streams: '',
            course: course?.[0]?.id ?? formData.course ?? null,
            target_countries: country ?? formData.country ?? null,
            city: formData.city ?? null,
            state: formData.state ?? null,
            intake_year: formData.year.toString() ?? null,
            college_name: college ?? null,
            exam: exam ?? null,
          },
        });
        if (response?.data?.message === 'User Saved Successfully') {
          setSubmitted(true);
          sessionStorage.setItem('leadFormSubmission', JSON.stringify({ submitted: true, returnUrl: pathname }));
          handleClose();
          router.push('/thank-you');
        } else if (response?.data?.message === 'OTP Sent Successfully') {
          setOtpSent(true);
        }
      } catch (error: any) {
        setErrorMessage(
          error?.response?.data?.error?.message ??
            error?.response?.data?.message ??
            error?.message ??
            'An error occurred. Please try again...',
        );
      }
    };

    const handleSubmit = async (event: React.FormEvent) => {
      event.preventDefault();

      if (formData.phone_number.length !== 10) {
        setErrorMessage('Phone number must be exactly 10 digits.');
        return;
      }

      if (!formData.city) {
        setErrorMessage("Please enter a valid city.");
        return;
      }

      if (!formData.course) {
        setErrorMessage("Please enter a valid course.");
        return;
      }

      if (
        (isStudyAbroad || isAbroadCountry) &&
        modalFields.some((field) => field.name === 'country') &&
        (!formData.country || formData.country.length === 0)
      ) {
        setErrorMessage('Please select a country.');
        return;
      }

      if (loading || isButtonDisabled) return;
      setIsButtonDisabled(true);
      try {
        setLoading(true);

        if (!otpSent) {
          await submitLeadForm();
        } else {
          await verifyOtp();
        }
      } catch (error) {
        setErrorMessage('An error occurred. Please try again.');
      } finally {
        setLoading(false);
        setTimeout(() => {
          setIsButtonDisabled(false);
        }, 3000);
      }
    };

    useEffect(() => {
      if (errorMessage) {
        const timer = setTimeout(() => {
          setErrorMessage('');
        }, 3000);
        return () => clearTimeout(timer);
      }
    }, [errorMessage]);

    useEffect(() => {
      let interval: NodeJS.Timeout;
      if (otpSent && timer > 0) {
        interval = setInterval(() => {
          setTimer((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              setShowResend(true);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
      return () => clearInterval(interval);
    }, [otpSent, timer]);

    const resendOtp = async (e: any) => {
      e.preventDefault();
      await submitLeadForm();
      setTimer(30);
      setShowResend(false);
    };
    return {
      handleClose,
      submitted,
      handleSubmit,
      handleInputChange,
      formData,
      setFormData,
      otpSent,
      setOtp,
      errorMessage,
      otp,
      title,
      source,
      showResend,
      resendOtp,
      timer,
      intakeYears,
      isAbroadCountry,
      handleToggleCourse,
      course,
      loading,
      modalFields,
    };
  };

  export default useLeadFormModalHook;
